import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, ChevronDown, ChevronUp, ShoppingBag, Sparkles, Trash2 } from 'lucide-react';
import CurrencyInputField from 'components/_UI/CurrencyInputField';
import Fieldset from 'components/_UI/Fieldset';
import Text from 'components/_UI/Text';
import { Column } from 'styled/alignment/Column';
import currency from 'helpers/currency';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import ImageProduct from '../../products/create/parts/ImageProduct';

/**
 * Formulário de um produto do evento, usado nos dois estados da etapa: rascunho
 * novo (com botão de remover) e produto que já está no evento (sem `onRemove` —
 * excluir produto não é operação desta tela). Nos dois casos a imagem é
 * editável: a atual aparece na miniatura e pode ser trocada ou removida.
 *
 * Só um cartão fica aberto por vez (`expanded`, controlado pelo pai) — com
 * vários produtos na lista, o formulário inteiro de cada um afogaria a etapa.
 * O conteúdo continua montado quando fechado para não perder o que já foi
 * digitado nem o arquivo escolhido no upload.
 *
 * Controlado localmente, sem react-hook-form, porque aqui existem N formulários
 * independentes numa lista.
 */
// O rascunho já saiu do painel de sugestões, mas o crachá viaja junto: quando o
// produtor revisar isso aqui embaixo, precisa lembrar que o conteúdo veio da IA.
const TYPE_LABELS = {
    product: 'Sugestão da IA',
    sales_promotion: 'Combo da IA',
    voucher: 'Voucher da IA',
};

export default function NewProductForm({
    product,
    domId,
    expanded = true,
    hasError = false,
    onToggle,
    onChange,
    onRemove,
}) {
    const [resetState, setResetState] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);

    const productName = product.name?.trim() || 'Novo produto';
    const isCollapsible = typeof onToggle === 'function';

    // Preview do arquivo recém-escolhido, pra miniatura do cabeçalho fechado
    // mostrar o que vai ser salvo e não a imagem antiga.
    const pickedImageUrl = useMemo(
        () => (product.imageFile ? URL.createObjectURL(product.imageFile) : null),
        [product.imageFile]
    );

    useEffect(() => {
        return () => {
            if (pickedImageUrl) URL.revokeObjectURL(pickedImageUrl);
        };
    }, [pickedImageUrl]);

    // Produto antigo pode ter imagem cujo arquivo não existe mais: aí o upload
    // começa vazio em vez de mostrar ícone de imagem quebrada.
    const currentImage = !imageFailed ? product.image : null;
    const thumbUrl = pickedImageUrl || currentImage;

    // `isEdit` do ImageUpload precisa de identidade estável: um objeto novo a
    // cada render zeraria o arquivo que o produtor acabou de escolher.
    const imageToEdit = useMemo(
        () => (currentImage ? { image: currentImage } : null),
        [currentImage]
    );

    function handleQuantityChange(value) {
        onChange({ quantity: value.replace(/[^0-9]/g, '') });
    }

    // Trocar a imagem de um produto que já existe: o arquivo novo substitui a
    // atual, e remover desanexa a que estava lá. `image` continua preenchido
    // com a foto antiga porque é ela que dá identidade estável ao upload — quem
    // manda no que vai ser salvo é `imageFile`.
    function handleImageChange(file) {
        onChange({ imageFile: file, image_id: null });
    }

    function handleImageDelete() {
        onChange({ imageFile: null, image: null, image_id: null });
        setImageFailed(false);
    }

    function handleHeaderKeyDown(event) {
        if (!isCollapsible) return;

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
        }
    }

    return (
        <Card id={domId} expanded={expanded} hasError={hasError && !expanded}>
            <CardHeader
                expanded={expanded}
                clickable={isCollapsible}
                onClick={isCollapsible ? onToggle : undefined}
                onKeyDown={handleHeaderKeyDown}
                role={isCollapsible ? 'button' : undefined}
                tabIndex={isCollapsible ? 0 : undefined}
                aria-expanded={isCollapsible ? expanded : undefined}
            >
                <CardHeaderLeft>
                    <CardIcon hasError={hasError && !expanded}>
                        {thumbUrl ? (
                            <HeaderThumb src={thumbUrl} alt='' onError={() => setImageFailed(true)} />
                        ) : hasError && !expanded ? (
                            <AlertCircle size={15} color={colors.danger} />
                        ) : (
                            <ShoppingBag size={15} color={colors.occasionalPurple} />
                        )}
                    </CardIcon>
                    <HeaderInfo>
                        <HeaderTitleRow>
                            <Text type='bold' color={colors.occasionalPurple} paddingBottom={0}>
                                {productName}
                            </Text>
                            {product.fromSuggestion && (
                                <OriginBadge>
                                    <Sparkles size={11} aria-hidden='true' />
                                    {TYPE_LABELS[product.type] || 'Sugestão da IA'}
                                </OriginBadge>
                            )}
                        </HeaderTitleRow>
                        {!expanded && (
                            <HeaderMeta>
                                {product.floatPrice > 0 && <span>{currency.format(product.floatPrice)}</span>}
                                {product.floatPrice > 0 && !!product.quantity && <MetaDot />}
                                {!!product.quantity && <span>{product.quantity} un.</span>}
                                {hasError && (
                                    <>
                                        {(product.floatPrice > 0 || !!product.quantity) && <MetaDot />}
                                        <MetaError>Faltam dados</MetaError>
                                    </>
                                )}
                            </HeaderMeta>
                        )}
                    </HeaderInfo>
                </CardHeaderLeft>

                <HeaderActions>
                    {onRemove && (
                        <RemoveBtn
                            type='button'
                            onClick={(event) => {
                                event.stopPropagation();
                                onRemove();
                            }}
                            aria-label={`Remover ${productName}`}
                        >
                            <Trash2 size={16} />
                        </RemoveBtn>
                    )}
                    {isCollapsible && (
                        <ChevronIcon>
                            {expanded
                                ? <ChevronUp size={18} color={colors.occasionalPurple} />
                                : <ChevronDown size={18} color={colors.night} />}
                        </ChevronIcon>
                    )}
                </HeaderActions>
            </CardHeader>

            <Content expanded={expanded}>
                <ContentInner>
                    <FieldsColumn verticalCenter>
                    <Fieldset
                        layout='squired'
                        label='Nome do produto'
                        value={product.name}
                        syncProp={true}
                        onChangeValue={(value) => onChange({ name: value })}
                    />
                    {product.showErrors && !product.name && (
                        <Text color={colors.danger} name='small' marginTop='4px'>Informe o nome do produto.</Text>
                    )}

                    <Fieldset
                        layout='squired'
                        marginTop='10px'
                        label='Descrição (opcional)'
                        value={product.description}
                        syncProp={true}
                        onChangeValue={(value) => onChange({ description: value })}
                    />

                    <Fieldset
                        layout='squired'
                        marginTop='10px'
                        label='Quantidade'
                        value={product.quantity}
                        syncProp={true}
                        onChangeValue={handleQuantityChange}
                    />
                    {product.showErrors && !product.quantity && (
                        <Text color={colors.danger} name='small' marginTop='4px'>Informe a quantidade.</Text>
                    )}

                    <CurrencyInputField
                        label='Preço'
                        layout='squired'
                        marginTop='10px'
                        value={product.price}
                        onChange={(value, name, values) => onChange({ price: value, floatPrice: values?.float || 0 })}
                    />
                    {product.showErrors && !(product.floatPrice > 0) && (
                        <Text color={colors.danger} name='small' marginTop='4px'>Informe o preço.</Text>
                    )}
                    </FieldsColumn>

                    <ImageColumn verticalCenter>
                        <ImageProduct
                            product
                            selectedProduct={imageToEdit}
                            onToggleImage={handleImageChange}
                            onDeleteFile={handleImageDelete}
                            resetState={resetState}
                        />
                    </ImageColumn>
                </ContentInner>
            </Content>
        </Card>
    );
}

const Card = styled.div.withConfig({
    shouldForwardProp: (prop) => !['expanded', 'hasError'].includes(prop),
})`
    background: ${colors.neutralLight};
    border: 1.5px solid ${({ expanded, hasError }) =>
        hasError ? colors.danger + '55'
            : expanded ? colors.occasionalPurple + '2E'
                : colors.night + '22'};
    border-radius: 14px;
    box-shadow: ${({ expanded }) => expanded ? `0 2px 12px ${colors.occasionalPurple}0F` : 'none'};
    margin-bottom: 12px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
`;

const CardHeader = styled.div.withConfig({
    shouldForwardProp: (prop) => !['expanded', 'clickable'].includes(prop),
})`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 16px;
    cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};
    border-bottom: 1px solid ${({ expanded }) => expanded ? colors.neutralDark : 'transparent'};
    transition: background 0.15s ease;

    ${({ clickable }) => clickable && `
        &:hover {
            background: ${colors.night}08;
        }

        &:focus-visible {
            outline: 2px solid ${colors.occasionalPurple};
            outline-offset: -2px;
        }
    `};
`;

const HeaderInfo = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const HeaderTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;

    & > p {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const HeaderMeta = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    font-size: 12px;
    color: ${colors.night};
`;

const MetaDot = styled.span`
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: ${colors.night}66;
`;

const MetaError = styled.span`
    font-weight: 600;
    color: ${colors.danger};
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
`;

const ChevronIcon = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
`;

const HeaderThumb = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
`;

const CardHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
    flex: 1;
`;

const OriginBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    padding: 3px 8px;
    border-radius: 20px;
    background: ${colors.occasionalPurple}12;
    color: ${colors.occasionalPurple};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
`;

const CardIcon = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'hasError',
})`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    flex-shrink: 0;
    overflow: hidden;
    background: ${({ hasError }) => hasError ? colors.danger + '15' : colors.occasionalPurple + '12'};
`;

const RemoveBtn = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: ${colors.danger};
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${colors.danger}12;
    }
`;

/* Fechado, o conteúdo continua montado (só escondido): o que foi digitado e o
   arquivo já escolhido no upload sobrevivem a abrir e fechar o cartão. */
const Content = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'expanded',
})`
    height: 0;
    opacity: 0;
    visibility: hidden;
    overflow: hidden;
    transition: opacity 0.2s ease;

    ${({ expanded }) => expanded && `
        height: auto;
        opacity: 1;
        visibility: visible;
        overflow: visible;
    `};
`;

const ContentInner = styled(Column)`
    align-items: center;
    gap: 4px;
    padding: 14px 16px 16px;

    @media screen and (min-width: ${breakpoints.sm}) {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
    }
`;

const FieldsColumn = styled(Column)`
    @media screen and (min-width: ${breakpoints.sm}) {
        flex: 1;
        min-width: 0;
    }
`;

const ImageColumn = styled(Column)`
    @media screen and (min-width: ${breakpoints.sm}) {
        width: 220px;
        flex-shrink: 0;
    }
`;
